import { ProductTagFull } from '@adapters/bigcommerce/types/product';
import { useMemo } from 'react';
import Tag, { TagProps } from '_components/UI/Tag';
import { usePdp } from '@context/PdpContext';
import { useTranslation } from 'next-i18next';
import { useHasMounted } from '@hooks/useHasMounted/useHasMounted';

import styles from './ProductMetaTags.module.scss';

type ProductMetaTagsProps = {
  productTags?: ProductTagFull[];
};

export type DisplayTag = {
  normalizedSource: string;
  name: string;
  color: TagProps['color'];
};

const normalizeTagLabel = (value?: string | null) =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Mock mapping of normalized tag label \u2192 Tag colour. Starting point for
 * per-tag badge styling; extend with more label/colour pairings (and localized
 * variants) as the design is defined. Keys are normalized via normalizeTagLabel.
 */
const TAG_COLOUR_BY_LABEL: Record<string, TagProps['color']> = {
  new: 'blue',
  nouveau: 'blue',
};

const DEFAULT_TAG_COLOUR: TagProps['color'] = 'gray';

const getTagColour = (label: string): TagProps['color'] =>
  TAG_COLOUR_BY_LABEL[normalizeTagLabel(label)] ?? DEFAULT_TAG_COLOUR;

export const getDisplayTags = (
  productTags: ReadonlyArray<{ name?: string | null } | null | undefined> = []
): DisplayTag[] => {
  const seenLabels = new Set<string>();

  return productTags.reduce<DisplayTag[]>((acc, tag) => {
    if (!tag?.name) {
      return acc;
    }

    const normalizedSource = normalizeTagLabel(tag.name);

    if (!normalizedSource || seenLabels.has(normalizedSource)) {
      return acc;
    }

    seenLabels.add(normalizedSource);
    acc.push({
      normalizedSource,
      name: tag.name,
      color: getTagColour(tag.name),
    });

    return acc;
  }, []);
};

const ProductMetaTags = ({ productTags = [] }: ProductMetaTagsProps) => {
  const { t } = useTranslation('pdp');
  const { promoBadge } = usePdp();
  const isClient = useHasMounted();

  const uniqueTags = useMemo(() => getDisplayTags(productTags), [productTags]);

  const showNewTag = isClient && promoBadge?.code === 'NEW';

  const displayTags = useMemo(
    () => [
      ...(showNewTag
        ? [
            {
              normalizedSource: 'promo-new',
              name: t('product.badge.new'),
              color: 'blue' as const,
            },
          ]
        : []),
      ...uniqueTags,
    ],
    [showNewTag, t, uniqueTags]
  );

  if (displayTags.length === 0) {
    return null;
  }

  return (
    <div className={styles.tagRow} data-testid="product-meta-tags">
      {displayTags.map((tag) => (
        <Tag
          key={`${tag.normalizedSource}-${tag.color}`}
          color={tag.color}
          size="medium"
          data-testid={`product-meta-tag-${normalizeTagLabel(tag.name)}-${
            tag.color
          }-${tag.normalizedSource}`}
          className={styles.tag}
        >
          {tag.name}
        </Tag>
      ))}
    </div>
  );
};

export default ProductMetaTags;
